import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentPreview = ({ template, config, onExport }) => {
  const [previewMode, setPreviewMode] = useState('document');

  const previewModes = [
    { id: 'document', label: 'Document', icon: 'FileText' },
    { id: 'outline', label: 'Outline', icon: 'List' },
    { id: 'metadata', label: 'Metadata', icon: 'Info' }
  ];

  const documentSections = [
    {
      id: 'cover',
      title: 'Cover Page',
      content: 'Document title, version, and organization branding'
    },
    {
      id: 'toc',
      title: 'Table of Contents',
      content: 'Automatically generated section navigation'
    },
    {
      id: 'executive',
      title: 'Executive Summary',
      content: 'High-level overview of the SAP implementation scope'
    },
    {
      id: 'scope',
      title: 'Scope Definition',
      content: 'Selected SAP Best Practice scope items and business processes'
    },
    {
      id: 'requirements',
      title: 'Requirements Analysis',
      content: 'Detailed requirements from questionnaire responses'
    },
    {
      id: 'fitgap',
      title: 'Fit/Gap Analysis',
      content: 'Analysis results with recommendations and customizations'
    },
    {
      id: 'diagrams',
      title: 'Process Diagrams',
      content: 'Visual representations of business processes and data flows'
    },
    {
      id: 'appendix',
      title: 'Appendix',
      content: 'Supporting documentation and reference materials'
    }
  ];

  const mockContent = `# Business Process Definition Document (BPD²)

## Executive Summary

This document outlines the SAP Best Practice implementation for ${config?.organizationName || 'Enterprise Solutions Ltd'}, focusing on the selected scope items and their integration requirements.

### Project Overview
- **Project Name:** ${config?.projectName || 'SAP S/4HANA Implementation'}
- **Version:** ${config?.version || '1.0'}
- **Author:** ${config?.author || 'SAP Architect'}
- **Date:** ${new Date()?.toLocaleDateString()}

## Scope Definition

### Selected SAP Best Practice Items

#### 1QM - Quality Management
Quality planning, inspection processes, and certificate management for manufacturing operations.

**Key Features:**
- Quality planning and control
- Inspection lot processing
- Certificate management
- Non-conformance handling

#### 5D2 - Procurement to Pay
End-to-end procurement process from purchase requisition to payment processing.

**Key Features:**
- Purchase requisition creation
- Vendor selection and approval
- Purchase order processing
- Goods receipt and invoice verification

## Requirements Analysis

### Functional Requirements

1. **Multi-Currency Support**
   - Status: Fit
   - Description: System must support transactions in multiple currencies with real-time exchange rates.

2. **External Tax Integration**
   - Status: Gap
   - Description: Integration with external tax calculation service for compliance.
   - Recommendation: Custom development required for API integration.

### Technical Requirements

- SAP S/4HANA 2023 or later
- Integration with existing ERP systems
- Real-time data synchronization
- Mobile-responsive interfaces

## Process Diagrams

[Diagram placeholders would be replaced with actual draw.io generated diagrams]

## Approval and Sign-off

This document requires approval from the following stakeholders:
- Business Process Owner
- IT Architecture Team
- Project Sponsor
- End User Representatives`;

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Eye" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Document Preview</h3>
        </div>
        <div className="flex items-center space-x-2">
          {previewModes?.map((mode) => (
            <button
              key={mode?.id}
              onClick={() => setPreviewMode(mode?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-colors ${
                previewMode === mode?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={mode?.icon} size={16} />
              <span>{mode?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Preview Content */}
      <div className="p-4">
        {previewMode === 'document' && (
          <div className="bg-white border border-border rounded-lg p-8 max-h-96 overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              <div className="text-center mb-8 pb-8 border-b border-border">
                <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} className="text-primary-foreground" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {template?.name || 'Business Process Definition'}
                </h1>
                <p className="text-muted-foreground">
                  {config?.organizationName || 'Enterprise Solutions Ltd'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Version {config?.version || '1.0'} • {new Date()?.toLocaleDateString()}
                </p>
              </div>
              <div className="whitespace-pre-line text-sm text-foreground leading-relaxed">
                {mockContent}
              </div>
            </div>
          </div>
        )}

        {previewMode === 'outline' && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-foreground mb-4">Document Structure</h4>
            {documentSections?.map((section, index) => (
              <div key={section?.id} className="flex items-start space-x-3 p-3 border border-border rounded-lg">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center text-sm font-medium text-muted-foreground">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h5 className="text-sm font-medium text-foreground">{section?.title}</h5>
                  <p className="text-xs text-muted-foreground mt-1">{section?.content}</p>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </div>
            ))}
          </div>
        )}

        {previewMode === 'metadata' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Document Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Template:</span>
                  <span className="text-foreground">{template?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="text-foreground">{template?.type}² Document</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Version:</span>
                  <span className="text-foreground">{config?.version || '1.0'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Author:</span>
                  <span className="text-foreground">{config?.author || 'SAP Architect'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created:</span>
                  <span className="text-foreground">{new Date()?.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Content Statistics</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sections:</span>
                  <span className="text-foreground">{documentSections?.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pages (Est.):</span>
                  <span className="text-foreground">12-15</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Diagrams:</span>
                  <span className="text-foreground">3</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Requirements:</span>
                  <span className="text-foreground">8</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scope Items:</span>
                  <span className="text-foreground">2</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
        <div className="text-sm text-muted-foreground">
          Last updated: {new Date()?.toLocaleString()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={() => onExport('pdf')}
          >
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            iconPosition="left"
            onClick={() => onExport('docx')}
          >
            Export DOCX
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Send"
            iconPosition="left"
            onClick={() => onExport('approval')}
          >
            Send for Approval
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;